import { FormProvider, FieldValues, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Stack, Box, IconButton } from '@mui/material'
import { X } from 'react-feather'
import {
  ControlledSelect,
  ControlledTextField,
  ControlledMediaUpload,
} from '@/components/inputs'
import { ObservationLogFormData } from '@/components/forms'
import {
  BATS_BIRDS_COUNTING_METHOD_OPTIONS,
  BATS_SPECIE_OPTIONS,
} from '../fieldsOptions'
import { theme } from '@/theme'
import { MediaType } from '@/types'
import { PatchMedia } from '@/hooks'

type SpecieFieldSetProps<T extends FieldValues> = {
  number: '' | '2' | '3' | '4'
  methods: UseFormReturn<T>
  disabledForTheUser: boolean
}

export const SpecieFieldSet = ({
  number,
  methods,
  disabledForTheUser,
}: SpecieFieldSetProps<ObservationLogFormData>): JSX.Element => {
  const { t } = useTranslation('observationLogs')

  const deleteSpecie = (number: string) => {
    methods.setValue(`specie${number}` as keyof ObservationLogFormData, '', {
      shouldDirty: true,
    })

    methods.setValue(`amount${number}` as keyof ObservationLogFormData, '', {
      shouldDirty: true,
    })

    methods.setValue(
      `counting_method${number}` as keyof ObservationLogFormData,
      '',
      { shouldDirty: true }
    )

    const soundRecordings = methods.getValues(
      `sound_recordings${number}` as keyof ObservationLogFormData
    ) as PatchMedia

    methods.setValue(
      `sound_recordings${number}` as keyof ObservationLogFormData,
      {
        chosenToUpload: [],
        uploadedToApi: [],
        stale: soundRecordings.uploadedToApi.map((i) => i.id),
        mediaType: MediaType.sound_recordings,
      },
      { shouldDirty: true }
    )
  }

  return (
    <FormProvider {...methods}>
      <Stack
        spacing={4}
        sx={{
          justifyContent: 'space-between',
          borderRadius: 2.5,
          backgroundColor: 'grey.200',
          width: '100%',
          p: 4,
        }}
      >
        <Stack direction="row" spacing={2} justifyContent="flex-start">
          <Box width="25%">
            <ControlledSelect
              control={methods.control}
              name={`specie${number}`}
              label={t('observation-log.fieldLabels.specieWithNumber', {
                number,
              })}
              options={BATS_SPECIE_OPTIONS}
              errorMessagePrefix=""
            />
          </Box>
          {methods.watch(`specie${number}`) && (
            <>
              <Box width="25%">
                <ControlledTextField
                  control={methods.control}
                  name={`amount${number}`}
                  placeholder={t(
                    'observation-log.fieldLabels.amountWithNumber',
                    {
                      number,
                    }
                  )}
                  type="number"
                  disabled={disabledForTheUser}
                  errorMessagePrefix=""
                />
              </Box>
              <Box width="25%">
                <ControlledSelect
                  control={methods.control}
                  name={`counting_method${number}`}
                  label={t(
                    'observation-log.fieldLabels.countingMethodWithNumber',
                    {
                      number,
                    }
                  )}
                  options={BATS_BIRDS_COUNTING_METHOD_OPTIONS}
                  disabled={disabledForTheUser}
                  errorMessagePrefix=""
                />
              </Box>
              <Box width="25%">
                <ControlledTextField
                  control={methods.control}
                  name={`recording_number${number}`}
                  placeholder={t(
                    'observation-log.fieldLabels.recordingNumberWithNumber',
                    { number }
                  )}
                  type="number"
                  errorMessagePrefix=""
                />
              </Box>
              {!!number && !disabledForTheUser && (
                <Box pt={2}>
                  <IconButton onClick={() => deleteSpecie(number)}>
                    <X size="16px" color={theme.palette.green['500']} />
                  </IconButton>
                </Box>
              )}
            </>
          )}
        </Stack>
        <ControlledMediaUpload
          name={`sound_recordings${number}`}
          control={methods.control}
          accept="audio/wav,audio/x-wav"
          soundRecord={true}
          label={t('observation-log.fieldLabels.specieSoundRecords', {
            number,
            maxNumber: 3,
          })}
          disabled={disabledForTheUser}
          errorMessagePrefix=""
        />
      </Stack>
    </FormProvider>
  )
}
